import styles from "./shift-details-stock-inventory.module.scss";
import { allFuelProductsMapper } from "./shift-details-stock-inventory.mapper";
import { allFuelProductsColumn } from "./shift-details-stock-inventory-column";
import { locale } from "localization/en";
import DataTableV2 from "components/commons/data-table/data-table-v2";
import useDataTable from "hooks/useDataTable";
import { find, sumBy } from "lodash";
import { initialShiftRecordFilterState } from "../../shift-records-filter.state";

const ShiftDetailsAllFuelProducts = ({ isShiftEndedCompletely, shift }) => {
  function nameToSku(name) {
    if (!name) return "";

    return name
      .toLowerCase()
      .replace(/\s+/g, "")
      .replace(/[^a-z0-9]/g, "");
  }

  const getPrice = (sku) => {
    return find(shift.shift.pricing, { product: sku })?.pricePerLiter || 0;
  };

  const computeTotalAmount = (items) => {
    return (
      sumBy(
        items.map((item) => {
          const discountedPumpPrice = getPrice(nameToSku(item.name)) - item.pumpDiscountPerItem;
          const originalTotalAmount = discountedPumpPrice * item.quantity;

          return {
            originalTotalAmount:
              item.vat === 0 && item.pumpDiscountPerItem !== 0
                ? ((getPrice(nameToSku(item.name)) ?? item.pumpPricePerItem) -
                    item.pumpDiscountPerItem / 1.12) *
                  item.quantity
                : originalTotalAmount,
          };
        }),
        "originalTotalAmount"
      ) || 0
    );
  };

  const computeVatExemptTotal = (items) => {
    return (
      sumBy(
        items.map((t) => {
          const originalTotalAmount = getPrice(nameToSku(t.name)) * t.quantity;
          const vatExempt = t.vat === 0 ? originalTotalAmount - originalTotalAmount / 1.12 : 0;
          return { vatExempt };
        }),
        "vatExempt"
      ) || 0
    );
  };

  const computeTotalDiscount = (items) => {
    return sumBy(
      items.map((t) => {
        const originalTotalAmount = getPrice(nameToSku(t.name)) * t.quantity;
        const pumpDiscountPerItem =
          t.vat === 0 && t.pumpDiscountPerItem > 0
            ? t.pumpDiscountPerItem / 1.12
            : t.pumpDiscountPerItem;

        const totalPumpDiscount = pumpDiscountPerItem * t.quantity;

        let totalDiscount = 0;
        if (t.vat === 0 && totalPumpDiscount > 0) {
          totalDiscount = originalTotalAmount - originalTotalAmount / 1.12;
        } else {
          const totalPromoDiscount =
            (t.vat === 0 ? t.totalDiscountPerItem / 1.12 : t.totalDiscountPerItem) * t.quantity;
          const vat = t.vat === 0 ? originalTotalAmount - originalTotalAmount / 1.12 : 0;
          totalDiscount = vat + totalPromoDiscount - totalPumpDiscount;
        }

        return {
          totalDiscount: totalDiscount,
        };
      }),
      "totalDiscount"
    );
  };

  const formatShiftValue = () => {
    const transactions = [
      ...shift.transactionItem.dieselTransactions,
      ...shift.transactionItem.gas91Transactions,
      ...shift.transactionItem.gas95Transactions,
      ...shift.transactionItem.gas97Transactions,
    ].filter(
      (t) =>
        !t.transaction.adjustmentType && t.pump !== "Pump 02" && t.transaction.status === "sale"
    );

    // const paymentMethods = [
    //   ...shift?.transactionPaymentMethod.cash,
    //   ...shift?.transactionPaymentMethod.nonCash,
    // ];

    // const totalAmount =
    //   sumBy(
    //     transactions.map((item) => ({
    //       originalTotalAmount:
    //         item.vat === 0 && item.pumpDiscountPerItem !== 0
    //           ? ((getPrice(nameToSku(item.name)) ?? item.pumpPricePerItem) -
    //               item.pumpDiscountPerItem / 1.12) *
    //             item.quantity
    //           : item.originalTotalAmount - item.totalPumpDiscount,
    //     })),
    //     "originalTotalAmount"
    //   ) || 0;
    // const displayTotalAmount =
    //   sumBy(
    //     transactions.map((item) => {
    //       return {
    //         originalTotalAmount:
    //           item.vat === 0 && item.pumpDiscountPerItem !== 0
    //             ? ((getPrice(nameToSku(item.name)) ?? item.pumpPricePerItem) -
    //                 item.pumpDiscountPerItem / 1.12) *
    //                 item.quantity +
    //               item.totalPumpDiscount
    //             : item.originalTotalAmount,
    //       };
    //     }),
    //     "originalTotalAmount"
    //   ) || 0;

    const totalAmount = computeTotalAmount(transactions);
    const totalPumpDiscount =
      sumBy(
        transactions.map((t) => {
          const pumpDiscountPerItem =
            t.vat === 0 && t.pumpDiscountPerItem > 0
              ? t.pumpDiscountPerItem / 1.12
              : t.pumpDiscountPerItem;

          const totalPumpDiscount = pumpDiscountPerItem * t.quantity;
          return {
            totalPumpDiscount: totalPumpDiscount,
          };
        }),
        "totalPumpDiscount"
      ) || 0;
    const totalDiscount = computeTotalDiscount(transactions);
    const totalAmountFuelSales = totalAmount - totalDiscount;
    // const totalAmount = sumBy(transactions, "originalTotalAmount") || 0;

    const totalLitersSold = sumBy(transactions, "quantity") || 0;
    // const totalOriginalAmountFuelSales =
    //   (sumBy(transactions, "grossTotalAmount") || 0) - totalPremiumDiscount;
    // const totalAmountFuelSales = sumBy(transactions, "netTotalAmount") || 0;
    const totalTotalizerDifference = sumBy(transactions, "totalizerDifference") || 0;
    const totalizerVariance = sumBy(transactions, "totalizerVariance") || 0;
    const totalDiscounts = Number(totalAmountFuelSales - totalAmount);
    const displayedTotalAmount = totalAmount + totalPumpDiscount;
    const vatExemptTotal = computeVatExemptTotal(transactions);
    const totalVatExempt = -vatExemptTotal;
    const totalDiscountPremium = totalDiscounts + vatExemptTotal;

    var allFuelData = {
      totalLitersSold: totalLitersSold,
      totalOriginalAmountFuelSales: displayedTotalAmount,
      totalDiscounts: totalDiscounts,
      totalVatExempt: totalVatExempt,
      totalDiscountPremium: totalDiscountPremium,
      totalAmountFuelSales: totalAmountFuelSales,
      totalTotalizerDifference: totalTotalizerDifference,
      totalizerVariance: totalizerVariance,
      totalPumpDiscount: totalPumpDiscount,
    };

    let list = [];
    list.push(allFuelData);
    return list;
  };

  const { table } = useDataTable({
    value: formatShiftValue(),
    filter: { initialState: initialShiftRecordFilterState(), mapper: (val) => val },
    table: {
      key: "shift",
      name: "allFuel",
      mapper: (val) => allFuelProductsMapper({ val, isShiftEndedCompletely }),
      columns: allFuelProductsColumn,
    },
  });

  return (
    <>
      <div className={styles.tableContainer}>
        <DataTableV2
          {...table}
          {...{ name: locale.allFuelProducts }}
          hideNoSearchResultRender
          tableVersion="v2"
          hidePagination
        />
      </div>
      <div className={styles.spacer50} />
    </>
  );
};

export default ShiftDetailsAllFuelProducts;
